import { FormControl, FormErrorMessage, SystemStyleObject, Textarea } from '@chakra-ui/core';
import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { useTranslation } from 'react-i18next';
import { QuestionnaireTemplateStore } from '../../../store/questionnaireTemplateModel';
import { smoothFocusItem } from '../../utils/smoothScroll';
import { useUpdateElementValue } from '../../hooks/useUpdateElementValue';
import { useFieldError } from '../../hooks/useFieldError';
import colors from '../../../../../../styles/colors';
import { QUESTIONNAIRE_ELEMENT_TYPE } from '../../../questionnaireTemplate.enums';
import { DEFAULT_MAX_LENGTH, VIDEO_RESPONSE_MAX_LENGTH } from '../../../questionnaireTemplate.constants';

const INPUT_STYLES: SystemStyleObject = {
  scrollbarColor: 'rgba(0, 0, 255, 0.3) rgba(0, 0, 255, 0.07)',
  scrollbarWidth: 'thin',
  resize: 'none',
  minHeight: 'unset',
  overflowY: 'auto',
  outline: '2px solid transparent',
  borderRadius: 'md',
  transition: 'outline-color 0.15s ease',
  p: 2,
  _focusWithin: {
    outlineColor: colors.blue[400],
    bg: 'rgba(0, 0, 255, 0.02)',
    p: 2,
  },
  '&[aria-invalid="true"]': {
    outlineColor: 'rgba(255, 0, 0, 0.4)',
    bg: 'rgba(255, 0, 0, 0.05)',
    p: 2,
  },
};

const FORM_CONTROL_PROPS: { ml: number; display: 'flex'; flexDirection: 'column' } = {
  ml: 0,
  display: 'flex',
  flexDirection: 'column',
};

const ELEMENT_PATH = 'question';

interface QuestionInputProps {
  id: string;
  // eslint-disable-next-line react/require-default-props
  paddingRight?: number;
}

function usePaddingRight(paddingRight: number | undefined): string | number {
  return React.useMemo(() => (paddingRight !== undefined ? `${paddingRight + 30}px` : 2), [paddingRight]);
}

export const QuestionInput = React.memo(function QuestionInput({ id, paddingRight = undefined }: QuestionInputProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const { t } = useTranslation('questionnaireTemplate');

  const value = QuestionnaireTemplateStore.useStoreState((s) => s.getElementValue(id, ELEMENT_PATH));
  const type = QuestionnaireTemplateStore.useStoreState((s) => s.getElementValue(id, 'questionType'));
  const setSelectedElementId = QuestionnaireTemplateStore.useStoreActions((a) => a.setSelectedElementId);
  const updateElementValue = useUpdateElementValue();
  const error = useFieldError({ elementId: id, path: ELEMENT_PATH });

  const maxLength =
    type === QUESTIONNAIRE_ELEMENT_TYPE.VIDEO_RESPONSE
      ? /* istanbul ignore next */ VIDEO_RESPONSE_MAX_LENGTH
      : DEFAULT_MAX_LENGTH;

  const paddingRightValue = usePaddingRight(paddingRight);

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      updateElementValue({ id, path: ELEMENT_PATH, value: e.target.value });
    },
    [id, updateElementValue],
  );

  // const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
  //   if (e.key === 'Enter') {
  //     e.preventDefault();
  //     e.currentTarget.blur();
  //   }
  // }, []);

  React.useEffect(() => {
    const el = textareaRef.current;
    if (el === null || value) return undefined;
    const timer = setTimeout(() => {
      smoothFocusItem(el);
      setSelectedElementId(id);
    }, 60);
    return () => clearTimeout(timer);
  }, [id, value, setSelectedElementId]);

  const hasError = Boolean(error);

  return (
    <FormControl isInvalid={hasError} {...FORM_CONTROL_PROPS}>
      <Textarea
        ref={textareaRef}
        data-error-key={`${id}.${ELEMENT_PATH}`}
        as={TextareaAutosize}
        minRows={1}
        maxRows={10}
        pr={paddingRightValue}
        variant="unstyled"
        value={value ?? /* istanbul ignore next */ ''}
        onChange={handleChange}
        // onKeyDown={handleKeyDown}
        placeholder={t('formBuilder.canvas.element.questionInputPlaceholder', { limit: maxLength })}
        sx={INPUT_STYLES}
      />
      {hasError && error !== undefined && (
        <FormErrorMessage pr={paddingRightValue} mt={0}>
          {t(error.message, error.context)}
        </FormErrorMessage>
      )}
    </FormControl>
  );
});

QuestionInput.displayName = 'QuestionInput';
